<template>
    <hts-drawer
        v-model="visible"
        color="main"
        :submit="!disabled ? submit : undefined"
        :title="$t('labels.workflowPosition')"
        size="large"
        @close="close">
        <hts-card
            :style="{ minWidth: minWidthScroll }"
            :title="$t('labels.generalInformation')">
            <hts-form-input
                v-model="data.index"
                :label="$t('labels.position')"
                disabled
                type="number" />

            <hts-form-input
                v-show="disabled"
                v-model="data.nodeTypeName"
                :label="$t('labels.nodeType')"
                disabled />

            <hts-admincp-select-node-type
                v-show="!disabled"
                v-model="data.nodeType"
                v-model:text="data.nodeTypeName"
                disabled
                key-id="_id"
                key-label="name"
                :filterable="true"
                :clearable="true"
                confirm-change
                :title-confirm="$t('labels.changeNodeType')"
                :detail-confirm="$t('labels.changeNodeTypeInfo')"
                @change="clearBusinessAndTask" />

            <div class="mt-5 mx-1">
                <p>
                    {{ $t('labels.businessAndTask') }}
                </p>
            </div>
            <div
                v-for="(business, index) in businessData"
                :key="business"
                class="pb-1">
                <hts-form-disclosure
                    :ref="
                        (el) => {
                            disclosureRefs[index] = el
                        }
                    "
                    :key="index"
                    color="main"
                    size="large"
                    :is-have-children="true">
                    <template #reference>
                        <div class="flex align-middle items-center gap-3 min-h-10 rounded-lg text-white bg-main-500 hover:bg-main-600 w-full">
                            <input
                                type="checkbox"
                                class="w-[17.5px] h-[17.5px]"
                                :checked="Object.keys(data.businesses).includes(business._id)"
                                :value="business._id"
                                disabled
                                @click="(e) => businessClick(e, business._id, business, index)" >
                            <span>
                                {{ business.name }}
                            </span>
                        </div>
                    </template>
                    <template #children>
                        <div
                            v-for="(task, subIndex) in business.tasks"
                            :key="task">
                            <div class="flex align-middle items-center gap-3 min-h-10 rounded-lg text-white mt-3 ml-10 p-2 pl-3 bg-main-400 hover:bg-main-600">
                                <input
                                    type="checkbox"
                                    class="w-[17.5px] h-[17.5px]"
                                    disabled
                                    :checked="Object.keys(data.businesses).includes(business._id) && data.businesses[business._id]?.includes(task._id)"
                                    :value="task._id"
                                    @click="(e) => taskClick(e, business._id, task._id)" >
                                <span>
                                    {{ task.name }}
                                </span>
                            </div>
                        </div>
                    </template>
                </hts-form-disclosure>
            </div>

            <div class="mt-5 mx-1 flex justify-start">
                <div>
                    {{ $t('labels.settingAndOrganizingTasks') }} <span
                        v-if="!disabled"
                        class="text-error-500">
                        *
                    </span>
                </div>
                <hts-tooltip>
                    <div>
                        <span class="font-bold">
                            {{ $t('labels.dragTaskToSortingTask') }}
                        </span>
                    </div>
                    <template #reference>
                        <hi-information-circle-icon class="flex items-center text-green-500 w-6 h-6" />
                    </template>
                </hts-tooltip>
            </div>

            <div
                :class="{ 'mt-2': index !== 0 }"
                class="flex items-center">
                <div class="w-12 font-bold" />
                <div class="px-3 py-1 box-border rounded-xl w-full flex items-center">
                    <div class="w-7" />
                    <div class="w-full ml-2">
                        <p class="font-bold">
                            {{ $t('labels.task') }}
                        </p>
                    </div>

                    <div class="flex justify-end gap-5 w-full font-bold">
                        <div class="h-full flex items-center justify-start w-52">
                            {{ $t('labels.performedBy') }}
                        </div>

                        <div class="h-full flex items-center justify-start w-52">
                            {{ $t('labels.activateVehicleStatus') }}
                        </div>

                        <div
                            class="flex items-center justify-start w-48"
                            style="pointer-events: auto !important">
                            {{ $t('labels.completionTimeHour') }}
                        </div>

                        <div
                            v-if="!disabled"
                            class="flex items-center justify-start w-10"
                            style="pointer-events: auto !important" />
                    </div>
                </div>
            </div>

            <div v-if="data.tasks && data.tasks.length">
                <div
                    v-for="(task, index) in data.tasks"
                    :key="task.key"
                    class="task-item"
                    :class="{ 'cursor-pointer': !disabled }">
                    <div
                        :class="{ 'mt-2': index !== 0 }"
                        class="flex items-center gap-2">
                        <div class="w-10 font-bold">
                            {{ index + 1 }}
                        </div>
                        <div class="drag-item bg-default-100 dark:bg-default-600 px-3 py-1 box-border rounded-xl w-full flex items-center gap-2">
                            <div class="w-7">
                                <hi-bars-3-icon
                                    class="h-6 w-6"
                                    aria-hidden="true" />
                            </div>
                            <div class="ml-2">
                                <p class="font-bold">
                                    {{ task.taskName }}
                                </p>
                                <p class="text-sm opacity-75 italic">
                                    {{ task.businessName }}
                                </p>
                            </div>

                            <div class="flex justify-end gap-5 w-full">
                                <div class="h-full flex items-center pointer-events-none w-52">
                                    <hts-dropdown
                                        class="flex items-center"
                                        :options="optionResponsibleForImplementation"
                                        :placement="'left'"
                                        label-key="label"
                                        :hover="false"
                                        :multiple-level="true"
                                        @click="(action) => handleResponsibleForImplementation(`${task.business}-${task.task}`, action)">
                                        <template #reference>
                                            <hts-tooltip class="mt-1">
                                                <div>
                                                    <span class="font-bold">
                                                        {{ $t('labels.responsibleForImplementation') }}
                                                    </span>
                                                </div>
                                                <template #reference>
                                                    <hts-form-button
                                                        v-if="!['HEADWAY_DRIVER', 'HEADWAY', 'CUSTOMER'].includes(task.responsibleForImplementation)"
                                                        size="small"
                                                        icon-name="hi-users-icon" />
                                                    <hts-form-button
                                                        v-else
                                                        size="small">
                                                        <span>
                                                            {{ labelResponsibleForImplementation(task.responsibleForImplementation) }}
                                                        </span>
                                                        <!--                            <div v-if="!disabled" class="inline-block align-middle hover:bg-gray-50 text-error-600 hover:text-error-400 cursor-pointer px-1 py-0"> -->
                                                        <!--                              <hi-x-circle-icon class="h-5 w-5" aria-hidden="true" @click="task.responsibleForImplementation = ''" /> -->
                                                        <!--                            </div> -->
                                                    </hts-form-button>
                                                </template>
                                            </hts-tooltip>
                                        </template>
                                    </hts-dropdown>
                                </div>

                                <div class="h-full flex items-center pointer-events-none w-52">
                                    <hts-dropdown
                                        class="flex items-center"
                                        :options="optionActivateVehicleStatus"
                                        :placement="'left'"
                                        label-key="label"
                                        :hover="false"
                                        @click="(action) => handleActivateVehicleStatus(`${task.business}-${task.task}`, action)">
                                        <template #reference>
                                            <hts-tooltip class="mt-1">
                                                <div>
                                                    <span class="font-bold">
                                                        {{ $t('labels.activateVehicleStatus') }}
                                                    </span>
                                                </div>
                                                <template #reference>
                                                    <hts-form-button
                                                        v-if="!['HAVE_GOODS', 'HAVE_CONTAINER', 'EMPTY_VEHICLE'].includes(task.activateVehicleStatus)"
                                                        size="small"
                                                        icon-name="hi-truck-icon" />
                                                    <hts-form-button
                                                        v-else
                                                        size="small"
                                                        text-transform="lowercase">
                                                        <span>
                                                            {{ activateVehicleStatusLabel(task.activateVehicleStatus) }}
                                                        </span>
                                                        <!--                            <div v-if="!disabled" class="inline-block align-middle hover:bg-gray-50 text-error-600 hover:text-error-400 cursor-pointer px-1 py-0"> -->
                                                        <!--                              <hi-x-circle-icon class="h-5 w-5" aria-hidden="true" @click="task.activateVehicleStatus = ''" /> -->
                                                        <!--                            </div> -->
                                                    </hts-form-button>
                                                </template>
                                            </hts-tooltip>
                                        </template>
                                    </hts-dropdown>
                                </div>

                                <div
                                    v-if="task.value"
                                    class="flex items-center justify-start w-52"
                                    style="pointer-events: auto !important">
                                    <hts-tooltip class="mt-1">
                                        <div>
                                            <span class="font-bold">
                                                {{ $t('labels.completionTime') }}
                                            </span>
                                        </div>
                                        <template #reference>
                                            <hts-form-button size="small">
                                                {{ task.value }} {{ $t('labels.hour') }}
                                            </hts-form-button>
                                        </template>
                                    </hts-tooltip>
                                </div>

                                <div
                                    class="flex items-center justify-start w-48"
                                    style="pointer-events: auto !important">
                                    <hts-tooltip class="mt-1">
                                        <div>
                                            <span class="font-bold">
                                                {{ $t('labels.completionTime') }}
                                            </span>
                                        </div>
                                        <template #reference>
                                            <!--                        {{ task.value }} {{ $t('labels.hour') }} -->
                                            <hts-form-input
                                                v-model="task.timeComplete"
                                                size="small"
                                                type="number"
                                                autocomplete="off"
                                                disabled
                                                name="completionTime"
                                                :prop="'task' + index" />
                                        </template>
                                    </hts-tooltip>
                                </div>

                                <hts-tooltip
                                    v-if="!disabled"
                                    class="w-10">
                                    <div>
                                        <span class="font-bold">
                                            {{ $t('labels.delete') }}
                                        </span>
                                    </div>
                                    <template #reference>
                                        <!--                    <hts-form-button -->
                                        <!--                      icon-name="hi-trash-icon" -->
                                        <!--                      @click="deleteTask(task.business, task.task)" -->
                                        <!--                    /> -->
                                        <hi-trash-icon
                                            :style="{ 'pointer-events': 'click' }"
                                            class="w-6 h-6 cursor-pointer"
                                            @click="deleteTask(task, index)" />
                                    </template>
                                </hts-tooltip>
                            </div>
                        </div>
                    </div>
                    <p
                        v-if="index === deleteTaskError"
                        class="text-error-500 text-right text-xs mt-1 mr-2">
                        {{
                            `Bạn không thể xóa tác vụ ${task.taskName} vì tác vụ này đang được cài đặt để kích hoạt trạng thái tải ${activateVehicleStatusLabel(
                                task.activateVehicleStatus
                            )}. Việc xóa tác vụ sẽ làm thay đổi  workflow`
                        }}
                    </p>
                </div>
            </div>
        </hts-card>
        <hts-modal
            :show="showPopupDeletedTask"
            :title="$t('labels.taskModule.delete')"
            type="confirm"
            :content="contentDeleteTask"
            @close="showPopupDeletedTask = false"
            @confirm="actionDeletedTask()" />
    </hts-drawer>
</template>
<script lang="ts" setup>
import { ITaskSetting, IWorkflowPosition } from '../../../../../admincp/workflow/-imports/form'
import { $urlSystem } from '#imports'

import screen from '~/components/utils/screen'
const { $disableReactive } = useNuxtApp()
const emits = defineEmits(['update'])
const store = $store()
const transportation = ref('')
const breakpoint = screen()
const minWidthScroll = computed(() => {
  return `${breakpoint.value.thresholds.md}px`
})

const disclosureRefs = ref([] as any)

const data = ref<IWorkflowPosition>({
  index: 0,
  nodeType: '',
  nodeTypeName: '',
  businesses: {} as { [key: string]: string[] },
  tasks: []
})

const visible = ref(false)
const businessData = ref<{ [key: string]: any }[]>([])
const actionDeletedTask = ref<Function>(() => {})
const showPopupDeletedTask = ref(false)
const disabled = ref(false)
const errorResponsibleForImplementation = ref(false)
const errorActiveVehicleStatusFirstTask = ref(false)
const errorActiveVehicleStatusLastTask = ref(false)
const deleteTaskError = ref(-1)
const contentDeleteTask = ref('')

const open = async (positionData: IWorkflowPosition, transportationData: string, disableData?: Boolean) => {
  data.value = { ...positionData }
  transportation.value = transportationData
  await getBusinessData(data.value.nodeType)
  if (disableData === true) {
    disabled.value = true
  }
  errorResponsibleForImplementation.value = false
  errorActiveVehicleStatusFirstTask.value = false
  errorActiveVehicleStatusLastTask.value = false
  visible.value = true
}

const dragOptions = {
  animation: 200
}

const resetFields = () => {
  data.value = {
    index: 0,
    nodeType: '',
    nodeTypeName: '',
    businesses: {},
    tasks: []
  }
  errorResponsibleForImplementation.value = false
  errorActiveVehicleStatusFirstTask.value = false
  errorActiveVehicleStatusLastTask.value = false
  businessData.value = [] as any
  disabled.value = false
  deleteTaskError.value = -1
}

const close = () => {
  resetFields()
  visible.value = false
}

const submit = () => {
  emits('update', $disableReactive(data.value) as IWorkflowPosition)
  close()
}

watch(
  () => data.value.nodeType,
  async (val) => {
    await getBusinessData(val)
  }
)

watch(
  () => data.value.tasks,
  () => {
    errorResponsibleForImplementation.value = false
    errorActiveVehicleStatusFirstTask.value = false
    errorActiveVehicleStatusLastTask.value = false
  },
  { deep: true }
)

const activateVehicleStatusLabel = (type: string) => {
  if (type === 'HAVE_GOODS') {
    return $t('labels.haveGoods')
  }
  if (type === 'HAVE_CONTAINER') {
    return $t('labels.haveContainer')
  }
  if (type === 'EMPTY_VEHICLE') {
    return $t('labels.emptyVehicle')
  }
}
const getBusinessData = async (nodeTypeId: string) => {
  // lấy thông tin nghiệp vụ
  const api = $urlSystem.business.getByNodeType
  if (!nodeTypeId) {
    return
  }

  const data: any = await $api(api, { uriParams: { ID: nodeTypeId } })
  const resData = data?.data?.value
  if (resData?.success) {
    // data update UI
    businessData.value = resData.data
    // data save
    const businesses: { [key: string]: string[] } = resData.data.reduce((acc: any, business: any) => {
      const taskIds = business.tasks.map((task: any) => task._id)
      acc[business._id] = taskIds
      return acc
    }, {})
    data.businesses = businesses
  }
}

const businessClick = (e: any, id: string, business: any, index: number) => {
  e.stopPropagation()
  if (disabled.value) {
    return
  }

  if (!Object.keys(data.value.businesses).includes(id)) {
    const taskIds = business.tasks.map((task: any) => task._id)
    data.value.businesses[id] = taskIds
    taskIds.forEach((taskId: string) => {
      addTaskData(id, taskId)
    })
    if (disclosureRefs.value[index] && typeof disclosureRefs.value[index]?.expand === 'function') {
      disclosureRefs.value[index].expand()
    }
    errorResponsibleForImplementation.value = false
    errorActiveVehicleStatusFirstTask.value = false
    errorActiveVehicleStatusLastTask.value = false
  } else {
    if (!showPopupDeletedTask.value) {
      showPopupDeletedTask.value = true
      actionDeletedTask.value = () => businessClick(e, id, business, index)
      e.preventDefault()
      return
    }

    const taskIds = data.value.businesses[id]
    const newObj = Object.keys(data.value.businesses).reduce((acc: any, key: any) => {
      if (key !== id) {
        acc[key] = data.value.businesses[key]
      }
      return acc
    }, {})
    taskIds.forEach((taskId: string) => {
      addTaskData(id, taskId, 'delete')
    })

    data.value.businesses = newObj

    showPopupDeletedTask.value = false
    actionDeletedTask.value = () => {}
  }
}

const taskClick = (e: any, businessId: string, taskId: string) => {
  if (disabled.value) {
    return
  }

  if (Object.keys(data.value.businesses).includes(businessId)) {
    if (!data.value.businesses[businessId].includes(taskId)) {
      data.value.businesses[businessId].push(taskId)
      addTaskData(businessId, taskId)
      errorResponsibleForImplementation.value = false
      errorActiveVehicleStatusFirstTask.value = false
      errorActiveVehicleStatusLastTask.value = false
    } else {
      if (!showPopupDeletedTask.value) {
        if (e) {
          e.preventDefault()
        }
        showPopupDeletedTask.value = true
        actionDeletedTask.value = () => taskClick(e, businessId, taskId)
        return
      }

      data.value.businesses[businessId] = data.value.businesses[businessId].filter((item: any) => item !== taskId)
      addTaskData(businessId, taskId, 'delete')
    }
  } else {
    addTaskData(businessId, taskId)
    data.value.businesses[businessId] = [taskId]
    errorResponsibleForImplementation.value = false
    errorActiveVehicleStatusFirstTask.value = false
    errorActiveVehicleStatusLastTask.value = false
  }

  actionDeletedTask.value = () => {}
  showPopupDeletedTask.value = false
}

const addTaskData = (businessId: string, taskId: string, action?: string) => {
  if (disabled.value) {
    return
  }

  const businessesData: { [key: string]: any } = store.data.getBusinesses
  const taskData: { [key: string]: any } = store.data.getTasks

  if (action === 'delete') {
    data.value.tasks = data.value.tasks.filter((task: any) => task.key !== `${businessId}-${taskId}`)
    return
  }

  const businessName = businessesData.filter((business: any) => businessId === business._id && !business.deleted)?.[0]?.name || ''
  const taskName = taskData.filter((task: any) => taskId === task._id && !task.deleted)?.[0]?.name || ''

  if (!businessName || !taskName) {
    return
  }

  data.value.tasks.push({
    key: `${businessId}-${taskId}`,
    business: businessId,
    businessName,
    task: taskId,
    taskName,
    responsibleForImplementation: '',
    activateVehicleStatus: ''
  })
}

const deleteTask = (taskData: any, index: number) => {
  if (taskData.activateVehicleStatus) {
    deleteTaskError.value = index
    return
  }

  const businessId = taskData.business
  const taskId = taskData.task
  if (disabled.value) {
    return
  }

  actionDeletedTask.value = () => {
    return taskClick('', businessId, taskId)
  }

  deleteTaskError.value = -1
  contentDeleteTask.value = `Tác vụ bị xoá sẽ không thể khôi phục. Bạn có muốn tiếp tục thực hiện thao tác xoá tác vụ ${taskData.taskName}`
  showPopupDeletedTask.value = true
}

const handleActivateVehicleStatus = (key: string, actionData: any) => {
  if (disabled.value) {
    return
  }

  data.value.tasks.forEach((task: ITaskSetting) => {
    if (task.key === key) {
      task.activateVehicleStatus = actionData.value
    }
  })
}

const handleResponsibleForImplementation = (key: string, actionData: any) => {
  if (disabled.value) {
    return
  }

  data.value.tasks.forEach((task: ITaskSetting) => {
    if (task.key === key) {
      task.responsibleForImplementation = actionData.value
    }
  })
}

const clearBusinessAndTask = () => {
  errorResponsibleForImplementation.value = false
  errorActiveVehicleStatusFirstTask.value = false
  errorActiveVehicleStatusLastTask.value = false
  data.value.tasks = []
  data.value.businesses = {}
}

const optionActivateVehicleStatus = computed(() => {
  if (transportation.value === 'CONTAINER') {
    return [
      {
        label: $t('labels.haveContainer'),
        class: 'text-blue-600 hover:text-blue-500 w-52 h-12',
        value: 'HAVE_CONTAINER'
      },
      {
        label: $t('labels.haveGoods'),
        class: 'text-blue-600 hover:text-blue-500 w-52 h-12',
        value: 'HAVE_GOODS'
      },
      {
        label: $t('labels.emptyVehicle'),
        class: 'text-blue-600 hover:text-blue-500 w-52 h-12',
        value: 'EMPTY_VEHICLE'
      }
    ]
  }

  if (transportation.value === 'NON_CONTAINER') {
    return [
      {
        label: $t('labels.haveGoods'),
        class: 'text-blue-600 hover:text-blue-500 w-52 h-12',
        value: 'HAVE_GOODS'
      },
      {
        label: $t('labels.emptyVehicle'),
        class: 'text-blue-600 hover:text-blue-500 w-52 h-12',
        value: 'EMPTY_VEHICLE'
      }
    ]
  }

  return []
})

const optionResponsibleForImplementation = computed(() => {
  return [
    {
      label: store.system.workspace.name,
      class: 'text-blue-600',
      tooltip: $t('labels.taskIsPerformedByDriverOrNot'),
      children: [
        {
          label: $t('labels.driver'),
          class: 'text-blue-600 hover:text-blue-500',
          value: 'HEADWAY_DRIVER'
        },
        {
          label: $t('labels.other'),
          class: 'text-blue-600 hover:text-blue-500',
          value: 'HEADWAY'
        }
      ]
    },
    {
      label: $t('labels.customer'),
      class: 'text-blue-600 hover:text-blue-500',
      value: 'CUSTOMER'
    }
  ]
})

const labelResponsibleForImplementation = (type: string) => {
  const workspaceName = store.system.workspace.name
  if (type === 'HEADWAY') {
    return workspaceName + ' - ' + $t('labels.other')
  }
  if (type === 'HEADWAY_DRIVER') {
    return workspaceName + ' - ' + $t('labels.driver')
  }
  if (type === 'CUSTOMER') {
    return $t('labels.customer')
  }
}

defineExpose({
  open
})
</script>
